import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";
import axiosInstance from "../../services/core/axiosInstance";
import CustomCard from "../../components/cards/CustomCard";
import { resetPassword } from "../../services/api/auth";
import { validateForgetPasswordFields } from "../../utils/validation/validation";

export const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token"); // Extracts UUID token from URL query string
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [passwords, setPasswords] = useState({
        password: "",
        confirmPassword: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswords((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationResult = validateForgetPasswordFields({
            token,
            password: passwords.password,
            confirmPassword: passwords.confirmPassword,
        });
        if (!validationResult.valid) {
            dispatch(showToast({ message: validationResult.message, severity: "error" }));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                token: token,
                newPassword: passwords.password,
            };
            const response = await resetPassword(payload);

            if (response.responseCode === 200) {
                dispatch(showToast({ message: response.message, severity: "success" }));
                navigate("/login");
            }
        } catch (error) {
            dispatch(showToast({ message: error.message, severity: "error" }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={[
                {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: { xs: "auto", md: "90vh" },
                },
                (theme) => ({
                    backgroundImage:
                        "radial-gradient(ellipse at 70% 51%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
                    backgroundSize: "cover",
                    ...theme.applyStyles("dark", {
                        backgroundImage:
                            "radial-gradient(at 70% 51%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
                    }),
                }),
            ]}
        >
            <CustomCard>
                <Typography component="h1" variant="h4" sx={{ width: "100%", mb: 1, fontWeight: 700 }}>
                    Reset Password
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Please enter your new password below.
                </Typography>
                <Divider />

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2.5 }}
                >
                    <FormControl>
                        <FormLabel htmlFor="password" sx={{ fontWeight: 500, mb: 1 }}>New Password</FormLabel>
                        <TextField
                            id="password"
                            name="password"
                            type="password"
                            placeholder="••••••"
                            required
                            fullWidth
                            value={passwords.password}
                            onChange={handleChange}
                            disabled={loading}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="confirmPassword" sx={{ fontWeight: 500, mb: 1 }}>Confirm New Password</FormLabel>
                        <TextField
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            placeholder="••••••"
                            required
                            fullWidth
                            value={passwords.confirmPassword}
                            onChange={handleChange}
                            disabled={loading}
                        />
                    </FormControl>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{ backgroundColor: "black", color: "white", py: 1.2, mt: 1 }}
                    >
                        {loading ? "Updating..." : "Update Password"}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate("/login")}> Cancel</Button>

                </Box>
            </CustomCard >
        </Box >
    );
};